'use client';

import { PositionRestrictionResponse } from '@/app/models/positionRestriction';
import {
  useDeletePositionRestrictionMutation,
  useSearchPositionRestrictionQuery,
} from '@/app/services/positionRestrictionApi';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, TableCell } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface PositionRestrictionTableProps {
  searchTerm: string;
}

export default function PositionRestrictionTable({ searchTerm }: PositionRestrictionTableProps) {
  const { data: positionRestrictions } = useSearchPositionRestrictionQuery(searchTerm);
  const { t } = useTranslation(['positionRestriction', 'common']);
  const [excludeDialog, setExcludeDialog] = useState(false);
  const [selectedRestriction, setSelectedRestriction] = useState<number | null>(null);

  const [deletePositionRestriction] = useDeletePositionRestrictionMutation();

  const handleDelete = async () => {
    if (!selectedRestriction) return;
    toast.info(t('excluding.restriction'));
    await deletePositionRestriction(selectedRestriction);
    toast.success(t('restriction.excluded'));
    setExcludeDialog(false);
    setSelectedRestriction(null);
  };

  const columns: Column[] = [
    { key: 'position', label: t('common:position') },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (restriction: PositionRestrictionResponse) => (
    <>
      <TableCell sx={bodyCellSx}>{`${restriction.position.name}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <IconButton
          size="small"
          onClick={() => {
            setSelectedRestriction(restriction.id);
            setExcludeDialog(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </>
  );

  // Use the restriction ID as the unique key
  const getRowKey = (restriction: PositionRestrictionResponse) => restriction.id;

  return (
    <>
      <DataTable
        columns={columns}
        data={positionRestrictions || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.results.found')}
        stickyHeader
        size="small"
      />

      <CancelOrConfirmDialog
        title={t('exclude.position.restriction')}
        contentText={t('confirm.position.restriction.allocation') as string}
        open={excludeDialog}
        onClose={() => setExcludeDialog(false)}
        onConfirm={() => {
          handleDelete();
        }}
      />
    </>
  );
}
